import React, { useState, useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { apiService, AffiliateCommissionPayload, AffiliateMember, AffiliateProgram, SubscriptionOrder, AffiliateCommissionStatus } from '../services/api';

interface AffiliateCommissionCreateModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const AffiliateCommissionCreateModal: React.FC<AffiliateCommissionCreateModalProps> = ({
  visible,
  onCancel,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    affiliateMemberId: '',
    programId: '',
    orderId: '',
    commissionAmount: '0',
    status: AffiliateCommissionStatus.PENDING,
  });
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<AffiliateMember[]>([]);
  const [programs, setPrograms] = useState<AffiliateProgram[]>([]);
  const [orders, setOrders] = useState<SubscriptionOrder[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [programSearchTerm, setProgramSearchTerm] = useState('');
  const [showProgramDropdown, setShowProgramDropdown] = useState(false);
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [showOrderDropdown, setShowOrderDropdown] = useState(false);

  useEffect(() => {
    if (visible) {
      fetchMembers();
      fetchPrograms();
      fetchOrders();
    }
  }, [visible]);

  const fetchMembers = async () => {
    setLoadingMembers(true);
    try {
      const response = await apiService.getAffiliateMembers({ limit: 100 });
      setMembers(response.members || []);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchPrograms = async () => {
    setLoadingPrograms(true);
    try {
      const response = await apiService.getAffiliatePrograms({ limit: 100 });
      setPrograms(response.programs || []);
    } catch (error) {
      console.error('Failed to fetch programs:', error);
    } finally {
      setLoadingPrograms(false);
    }
  };

  const fetchOrders = async () => {
    setLoadingOrders(true);
    try {
      const response = await apiService.getSubscriptionOrders({ limit: 100 });
      setOrders(response.orders || []);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const filteredMembers = members.filter(member => {
    if (!memberSearchTerm) return true;
    const searchLower = memberSearchTerm.toLowerCase();
    return (
      member.code?.toLowerCase().includes(searchLower) ||
      member.user?.email?.toLowerCase().includes(searchLower) ||
      member.user?.name?.toLowerCase().includes(searchLower)
    );
  });

  const filteredPrograms = programs.filter(program => {
    if (!programSearchTerm) return true;
    const searchLower = programSearchTerm.toLowerCase();
    return program.name?.toLowerCase().includes(searchLower);
  });

  const filteredOrders = orders.filter(order => {
    if (!orderSearchTerm) return true;
    const searchLower = orderSearchTerm.toLowerCase();
    return order.orderNumber?.toLowerCase().includes(searchLower);
  });

  const selectedMember = members.find(m => m.id === parseInt(formData.affiliateMemberId));
  const selectedProgram = programs.find(p => p.id === parseInt(formData.programId));
  const selectedOrder = orders.find(o => o.id === parseInt(formData.orderId));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.affiliateMemberId || !formData.programId || !formData.orderId) {
      alert('Please select affiliate member, program, and order');
      return;
    }

    try {
      setLoading(true);

      const payload: AffiliateCommissionPayload = {
        affiliateMemberId: parseInt(formData.affiliateMemberId),
        programId: parseInt(formData.programId),
        orderId: parseInt(formData.orderId),
        commissionAmount: parseFloat(formData.commissionAmount) || 0,
        status: formData.status,
      };

      await apiService.createAffiliateCommission(payload);
      resetForm();
      onSuccess();
    } catch (error: any) {
      console.error('Failed to create commission:', error);
      alert(error.message || 'Failed to create commission');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      affiliateMemberId: '',
      programId: '',
      orderId: '',
      commissionAmount: '0',
      status: AffiliateCommissionStatus.PENDING,
    });
    setMemberSearchTerm('');
    setProgramSearchTerm('');
    setOrderSearchTerm('');
    setShowMemberDropdown(false);
    setShowProgramDropdown(false);
    setShowOrderDropdown(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-gray-900">Create Affiliate Commission</h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Affiliate Member */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Affiliate Member <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search affiliate member..."
                    value={memberSearchTerm}
                    onChange={(e) => {
                      setMemberSearchTerm(e.target.value);
                      setShowMemberDropdown(true);
                      if (!e.target.value) {
                        setFormData(prev => ({ ...prev, affiliateMemberId: '' }));
                      }
                    }}
                    onFocus={() => setShowMemberDropdown(true)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imola-primary"
                    disabled={loadingMembers}
                    required
                  />
                </div>
                {showMemberDropdown && (
                  <>
                    <div className="fixed inset-0 z-20" onClick={() => setShowMemberDropdown(false)} />
                    <div className="absolute z-30 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredMembers.length === 0 ? (
                        <div className="px-4 py-2 text-sm text-gray-500">No members found</div>
                      ) : (
                        filteredMembers.map(member => (
                          <button
                            key={member.id}
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, affiliateMemberId: member.id.toString() }));
                              setMemberSearchTerm(member.code);
                              setShowMemberDropdown(false);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100"
                          >
                            <div className="text-sm font-medium text-gray-900">{member.code}</div>
                            {member.user && (
                              <div className="text-xs text-gray-500">{member.user.email}</div>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
              {selectedMember && (
                <div className="mt-2 p-2 bg-gray-50 rounded-md">
                  <div className="text-sm font-medium text-gray-900">{selectedMember.code}</div>
                </div>
              )}
            </div>

            {/* Program */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Program <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search program..."
                    value={programSearchTerm}
                    onChange={(e) => {
                      setProgramSearchTerm(e.target.value);
                      setShowProgramDropdown(true);
                      if (!e.target.value) {
                        setFormData(prev => ({ ...prev, programId: '' }));
                      }
                    }}
                    onFocus={() => setShowProgramDropdown(true)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imola-primary"
                    disabled={loadingPrograms}
                    required
                  />
                </div>
                {showProgramDropdown && (
                  <>
                    <div className="fixed inset-0 z-20" onClick={() => setShowProgramDropdown(false)} />
                    <div className="absolute z-30 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredPrograms.length === 0 ? (
                        <div className="px-4 py-2 text-sm text-gray-500">No programs found</div>
                      ) : (
                        filteredPrograms.map(program => (
                          <button
                            key={program.id}
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, programId: program.id.toString() }));
                              setProgramSearchTerm(program.name);
                              setShowProgramDropdown(false);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100"
                          >
                            <div className="text-sm font-medium text-gray-900">{program.name}</div>
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
              {selectedProgram && (
                <div className="mt-2 p-2 bg-gray-50 rounded-md">
                  <div className="text-sm font-medium text-gray-900">{selectedProgram.name}</div>
                </div>
              )}
            </div>

            {/* Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Order <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search order..."
                    value={orderSearchTerm}
                    onChange={(e) => {
                      setOrderSearchTerm(e.target.value);
                      setShowOrderDropdown(true);
                      if (!e.target.value) {
                        setFormData(prev => ({ ...prev, orderId: '' }));
                      }
                    }}
                    onFocus={() => setShowOrderDropdown(true)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imola-primary"
                    disabled={loadingOrders}
                    required
                  />
                </div>
                {showOrderDropdown && (
                  <>
                    <div className="fixed inset-0 z-20" onClick={() => setShowOrderDropdown(false)} />
                    <div className="absolute z-30 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredOrders.length === 0 ? (
                        <div className="px-4 py-2 text-sm text-gray-500">No orders found</div>
                      ) : (
                        filteredOrders.map(order => (
                          <button
                            key={order.id}
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, orderId: order.id.toString() }));
                              setOrderSearchTerm(order.orderNumber);
                              setShowOrderDropdown(false);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100"
                          >
                            <div className="text-sm font-medium text-gray-900">{order.orderNumber}</div>
                            <div className="text-xs text-gray-500">
                              {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(order.totalAmount)}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
              {selectedOrder && (
                <div className="mt-2 p-2 bg-gray-50 rounded-md">
                  <div className="text-sm font-medium text-gray-900">{selectedOrder.orderNumber}</div>
                </div>
              )}
            </div>

            {/* Commission Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Commission Amount (IDR) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.commissionAmount}
                onChange={(e) => setFormData(prev => ({ ...prev, commissionAmount: e.target.value }))}
                min="0"
                step="0.01"
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as AffiliateCommissionStatus }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
              >
                <option value={AffiliateCommissionStatus.PENDING}>Pending</option>
                <option value={AffiliateCommissionStatus.CONFIRMED}>Confirmed</option>
                <option value={AffiliateCommissionStatus.PAID}>Paid</option>
                <option value={AffiliateCommissionStatus.VOID}>Void</option>
              </select>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-imola-primary text-white rounded-md hover:bg-imola-primary/80 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Commission'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AffiliateCommissionCreateModal;

